/**
 * useRealtimeNotifications — Laravel Echo hook for live notification updates.
 *
 * Migrated from Socket.io to Laravel Echo + Reverb.
 *
 * Channel  : private  →  App.Models.User.{userId}
 * Events   : .NotificationNew  .ToastShow  .NotificationRead  .NotificationSync
 *
 * Laravel events (server):
 *   event(new NotificationNew($userId, ...));
 *   event(new ToastShow($userId, ...));
 *   event(new NotificationRead($userId, $notificationId));
 *
 * markRead / markAllRead emit via HTTP (the API triggers the server event).
 * No socket.emit needed — just call your existing API endpoint.
 *
 * Usage:
 *   const { notifications, toasts, unreadCount, dismissToast, markRead }
 *     = useRealtimeNotifications();
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useEcho } from '@context/EchoContext';
import { useAuth } from '@hooks/useAuth';

const MAX_TOASTS = 5;
const TOAST_TTL  = 8000; // 8 s

export default function useRealtimeNotifications() {
  const { echo, connected } = useEcho();
  const { user }            = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [toasts, setToasts]               = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const toastTimers = useRef(new Map());
  const channelRef  = useRef(null);

  // ── Shared toast helper ──────────────────────────────────────────────────
  const addToast = useCallback((toast) => {
    const id          = toast.id || `toast_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const toastWithId = { ...toast, id, createdAt: new Date() };

    setToasts(prev => [toastWithId, ...prev].slice(0, MAX_TOASTS));

    const timer = setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      toastTimers.current.delete(id);
    }, TOAST_TTL);
    toastTimers.current.set(id, timer);
  }, []);

  // ── Subscribe ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!echo || !connected || !user?.id) return;

    // private-App.Models.User.{userId}
    const channel = echo.private(`App.Models.User.${user.id}`);
    channelRef.current = channel;

    // Persistent notification (DB-backed)
    // Server event: App\Events\Notifications\NotificationNew
    channel.listen('.NotificationNew', (notification) => {
      setNotifications(prev => [notification, ...prev].slice(0, 50));
      setUnreadCount(prev => prev + 1);
      addToast({
        id:      notification.id || `notif_${Date.now()}`,
        type:    notification.type || 'info',
        title:   notification.title,
        message: notification.message,
        icon:    notification.icon,
        link:    notification.link,
      });
      // Re-broadcast inside the tab so feature components (the
      // coordinator inbox, etc.) can react to specific notification
      // types without each opening their own Echo connection.
      try {
        window.dispatchEvent(new CustomEvent('vowsera:notification', { detail: notification }));
      } catch { /* old browser */ }
    });

    // Ephemeral toast (no DB entry)
    // Server event: App\Events\Notifications\ToastShow
    channel.listen('.ToastShow', (toast) => {
      addToast(toast);
    });

    // Another tab/device marked a notification as read
    // Server event: App\Events\Notifications\NotificationRead
    channel.listen('.NotificationRead', (data) => {
      setUnreadCount(prev => Math.max(0, prev - (data.count || 1)));
      if (data.notification_id) {
        setNotifications(prev =>
          prev.map(n => n.id === data.notification_id ? { ...n, isRead: true } : n)
        );
      }
    });

    // Bulk unread count sync (e.g., on reconnect)
    // Server event: App\Events\Notifications\NotificationSync
    channel.listen('.NotificationSync', (data) => {
      setUnreadCount(data.count || 0);
    });

    return () => {
      echo.leave(`App.Models.User.${user.id}`);
      channelRef.current = null;
      toastTimers.current.forEach(t => clearTimeout(t));
    };
  }, [echo, connected, user?.id, addToast]);

  // ── Public API ───────────────────────────────────────────────────────────

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    if (toastTimers.current.has(id)) {
      clearTimeout(toastTimers.current.get(id));
      toastTimers.current.delete(id);
    }
  }, []);

  /**
   * markRead — call your HTTP PATCH /api/notifications/{id}/read endpoint.
   * The controller dispatches NotificationRead, which Echo broadcasts
   * to all other devices; we also update local state immediately.
   */
  const markRead = useCallback((notificationId) => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
    // HTTP call handled by the caller (React Query mutation, etc.)
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);
    // HTTP call handled by the caller
  }, []);

  return {
    notifications,
    toasts,
    unreadCount,
    setUnreadCount,
    dismissToast,
    markRead,
    markAllRead,
    connected,
  };
}
